/* eslint-disable no-param-reassign */
import * as React from 'react';
import classnames from 'classnames';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { Link } from 'libs/router';

import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import SVGIcon from 'components/svg-icon/SVGIcon';
import ResponseTimeInfo from 'components/response-time-info/ResponseTimeInfo';
import NotificationDot from 'components/notification-dot/NotificationDot';
import MoreButton from 'components/buttons/more-button/MoreButton';
import StatusLabel from 'components/status-label/StatusLabel';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { __t, applyTranslations } from 'utils/language';
import { STATUS_LABEL_MAP, addThousandsSeparator, getPercentage } from 'utils/helper/common';
import { renderPrice } from 'utils/helper/price';
import { getEntityFromProfile } from 'utils/view';
import { CHANNEL_TYPE_INSTAGRAM, getChannelUserNameWithFallback } from 'utils/channels';
import { getFormattedFollowersValue, getNumFollowers, mapResponseTypeToCategory } from 'utils/influencers';
import { getFormattedPercentageRate } from 'utils/formatters';
import { insertIf } from 'utils/list';
import {
  AVATAR_SIZE,
  CREATOR_NAME_SIZE,
  ENGAGEMENT,
  FOLLOWERS,
  METRICS_MIN,
  RESPONSE_TIME_SORTING_PRIORITY,
  SORTING_PRIORITY,
  getCreatorPopoverComponent,
  getFilteredChannels
} from 'modules/subcampaigns/components/subcampaigns-table/subcampaigns-table-columns';
import {
  CCM_CHANNEL_TYPES,
  CCM_OFFER_TYPE_POOL,
  hasSubmittedOutfits
} from '../../utils/ccm-campaign-offers';

applyTranslations(require('modules/subcampaigns/components/subcampaigns-table/subcampaigns-table.i18n.json'));
applyTranslations(require('../ccm-campaign-offers-table.i18n.json'));

import type {
  SortableTableColumnType,
  Subcampaign,
  SubcampaignInfluencerProfile,
  SubcampaignOfferDeliverable,
  TableAlign
} from 'types';
import type {
  SORTING_METRICS_TYPE
} from 'modules/subcampaigns/components/subcampaigns-table/subcampaigns-table-columns';
import type {
  CcmOfferContextMenuActions,
  CcmOfferResult,
  CcmOfferRow,
  CcmOfferTab
} from '../../types';

const SUBCAMPAIGNS_MSG_KEY = 'subcampaigns_table';
const MSG_KEY = 'ccm_campaign_offers_table';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

const padOutfits = (
  outfits: Array<SubcampaignOfferDeliverable>
): Array<SubcampaignOfferDeliverable> => (
  isEmpty(outfits)
    ? [{ id: 0, name: '-' } as SubcampaignOfferDeliverable]
    : outfits
);

const getMetric = (
  creatorProfile: SubcampaignInfluencerProfile,
  channel: typeof SORTING_PRIORITY[number],
  metricType: SORTING_METRICS_TYPE
) => (
  creatorProfile.channels[channel]?.metrics?.[metricType] || 0
);

const getSortingByMetrics = (metricType: SORTING_METRICS_TYPE) =>
  (a: SubcampaignInfluencerProfile, b: SubcampaignInfluencerProfile) => {
    const metrics = SORTING_PRIORITY.reduce((prevValue, channel) => {
      const metricsValueA = getMetric(a, channel, metricType);
      const metricsValueB = getMetric(b, channel, metricType);

      prevValue.metricsValueA[channel] = metricsValueA >= METRICS_MIN[metricType] ? metricsValueA : 0;
      prevValue.metricsValueB[channel] = metricsValueB >= METRICS_MIN[metricType] ? metricsValueB : 0;

      return prevValue;
    }, { metricsValueA: {}, metricsValueB: {} });

    const diff = SORTING_PRIORITY.reduce((prevValue: number, channel) => {
      if ((metrics.metricsValueA[channel] - metrics.metricsValueB[channel]) && !prevValue) {
        return metrics.metricsValueA[channel] - metrics.metricsValueB[channel];
      }

      return prevValue;
    }, 0);

    // in case of metric being 0 for both creator profiles, order will be decided on followers
    return diff === 0 && metricType !== FOLLOWERS
      ? getSortingByMetrics(FOLLOWERS)(a, b)
      : diff;
  };

const getCreatorColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.creator`),
  key: 'creator',
  width: activeTab !== 'accepted' ? 200 : 180,
  sort: (a: CcmOfferRow, b: CcmOfferRow) => (
    a.creatorProfile.name.localeCompare(b.creatorProfile.name)
  ),
  render: ({ creatorProfile, activeTab: currentActiveTab }: CcmOfferRow) => {
    const entity = getEntityFromProfile(creatorProfile);
    const entityUrl = `/content-creator/profiles/${entity?.id ?? ''}`;

    const containerClasses = classnames(
      'subcampaigns-table__cell',
      'subcampaigns-table__cell--creator',
      'subcampaigns-table__cell--padded'
    );

    const isPool = currentActiveTab === CCM_OFFER_TYPE_POOL;
    const message = isPool ? __t(`${SUBCAMPAIGNS_MSG_KEY}.tooltips.creator`) : undefined;

    const Component = isPool
      ? undefined
      : getCreatorPopoverComponent(creatorProfile as SubcampaignInfluencerProfile);

    return (
      <div className={containerClasses}>
        <Tooltip
          width={isPool ? 'normal' : 'auto-compact'}
          message={message}
          component={Component}
          classNameWrapper="ccm-campaign-offers-table__tooltip-creator text-overflow-ellipsis"
        >
          <Entity>
            <EntityAvatar
              bordered
              customSize={AVATAR_SIZE}
              entity={entity}
              link={entityUrl}
              openInNewTab
            />
            <EntityName
              maxLength={CREATOR_NAME_SIZE.maxWidth}
              style={CREATOR_NAME_SIZE}
              size="small"
              entity={entity}
              link={entityUrl}
              openInNewTab
              hideTitle
            />
          </Entity>
        </Tooltip>
      </div>
    );
  }
});

const getCountryColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.creator_country`),
  key: 'creator_country',
  customizable: true,
  width: activeTab !== 'accepted' ? 88 : 78,
  sort: (a: CcmOfferRow, b: CcmOfferRow, ascending?: boolean) => {
    const countryCodeA = isArray(a.creatorProfile.locations) ? a.creatorProfile.locations[0]?.countryCode : null;
    const countryCodeB = isArray(b.creatorProfile.locations) ? b.creatorProfile.locations[0]?.countryCode : null;

    if (!countryCodeA) {
      return 1;
    }

    if (!countryCodeB) {
      return -1;
    }

    const localeCompare = countryCodeA.localeCompare(countryCodeB);

    if (localeCompare === 0) {
      return ascending
        ? getSortingByMetrics(FOLLOWERS)(b.creatorProfile, a.creatorProfile)
        : getSortingByMetrics(FOLLOWERS)(a.creatorProfile, b.creatorProfile);
    }

    return localeCompare;
  },
  render: ({ creatorProfile }: CcmOfferRow) => {
    if (isArray(creatorProfile.locations)) {
      return (
        <div className="subcampaigns-table__cell-rows">
          <div
            title={creatorProfile.locations[0]?.country}
            className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered"
          >
            {creatorProfile.locations[0]?.countryCode}
          </div>
        </div>
      );
    }

    return null;
  }
});

const renderChannels = (
  { creatorProfile }: CcmOfferRow,
  showUserName: boolean
): React.ReactNode => {
  const channels = creatorProfile.channels;
  const filteredChannels = getFilteredChannels(
    channels,
    CCM_CHANNEL_TYPES,
    false
  );

  return (
    <div className="subcampaigns-table__cell-rows">
      {
        filteredChannels.map(channelType => {
          const channel = channels[channelType];
          let username = getChannelUserNameWithFallback(channel);

          const containerClasses = classnames('subcampaigns-table__cell-row', {
            [`subcampaigns-table__cell-${channelType}-metric`]: true
          });

          if (channelType === CHANNEL_TYPE_INSTAGRAM) {
            username = `@${username}`;
          }

          const channelClasses = classnames('subcampaigns-table__channel', {
            'subcampaigns-table__channel--centered': !showUserName
          });

          return (
            <div
              key={channelType}
              className={containerClasses}
            >
              <div className={channelClasses}>
                <Tooltip
                  message={__t(`${SUBCAMPAIGNS_MSG_KEY}.tooltips.channel_${channelType}`)}
                >
                  <div className="subcampaigns-table__channel-icon">
                    <SVGIcon
                      name={`${channelType}-no-customisation`}
                      width={16}
                      height={16}
                      link={channel.url}
                    />
                  </div>
                </Tooltip>
                {
                  showUserName && (
                    <div
                      className="subcampaigns-table__channel-name"
                      title={username}
                    >
                      {username}
                    </div>
                  )
                }
              </div>
            </div>
          );
        })
      }
    </div>
  );
};

const getWideChannelColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.channel`),
  key: 'channels',
  align: 'center' as TableAlign,
  width: activeTab !== 'accepted' ? 216 : 114,
  render: (col: CcmOfferRow) => (
    renderChannels(col, true)
  )
});

const getChannelColumn = () => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.channel`),
  key: 'channels',
  align: 'center' as TableAlign,
  width: 76,
  render: (col: CcmOfferRow) => (
    renderChannels(col, false)
  )
});

const getTierColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.creator_tier`),
  key: 'creator_tier',
  align: 'center' as TableAlign,
  customizable: true,
  width: activeTab !== 'accepted' ? 96 : 68,
  sort: (a: CcmOfferRow, b: CcmOfferRow) => (
    getSortingByMetrics(FOLLOWERS)(a.creatorProfile, b.creatorProfile)
  ),
  render: ({ creatorProfile }: CcmOfferRow) => {
    const tierLabel = creatorProfile.tier
      ? __t(`${SUBCAMPAIGNS_MSG_KEY}.creator_tier.${creatorProfile.tier}`)
      : '-';

    const containerClasses = classnames(
      'subcampaigns-table__cell-row',
      'subcampaigns-table__cell-row--centered',
      'subcampaigns-table__cell-instagram-metric'
    );

    return (
      <div className="subcampaigns-table__cell-rows">
        <div className={containerClasses}>
          {tierLabel}
        </div>
      </div>
    );
  }
});

const getFollowersColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: (
    <div className="subcampaigns-table__head-icon">
      <Tooltip
        message={__t(`${SUBCAMPAIGNS_MSG_KEY}.tooltips.followers`)}
      >
        <SVGDynamicIcon
          name="users-connected-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </Tooltip>
    </div>
  ),
  width: activeTab !== 'accepted' ? 80 : 62,
  key: 'followers',
  customizable: true,
  sort: (a: CcmOfferRow, b: CcmOfferRow) => (
    getSortingByMetrics(FOLLOWERS)(a.creatorProfile, b.creatorProfile)
  ),
  render: ({ creatorProfile }: CcmOfferRow) => {
    const channels = creatorProfile.channels;
    const filteredChannels = getFilteredChannels(
      channels,
      CCM_CHANNEL_TYPES,
      false
    );

    return (
      <div className="subcampaigns-table__cell-rows">
        {
          filteredChannels.map(channelType => {
            const channel = channels[channelType];
            const metrics = channel.metrics;
            const numFollowers = getNumFollowers(metrics.followers);
            const formattedFollowers = getFormattedFollowersValue(numFollowers);

            const containerClasses = classnames('subcampaigns-table__cell-row', {
              'subcampaigns-table__cell-row--centered': true,
              [`subcampaigns-table__cell-${channelType}-metric`]: true
            });

            return (
              <div
                key={channelType}
                className={containerClasses}
              >
                <div className="subcampaigns-table__metric">
                  {formattedFollowers}
                </div>
              </div>
            );
          })
        }
      </div>
    );
  }
});

const getEngagementColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: (
    <div className="subcampaigns-table__head-icon">
      <Tooltip
        message={__t(`${SUBCAMPAIGNS_MSG_KEY}.tooltips.engagement`)}
      >
        <SVGDynamicIcon
          name="rotation-arrows-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </Tooltip>
    </div>
  ),
  width: activeTab !== 'accepted' ? 80 : 64,
  key: 'engagement_rate',
  customizable: true,
  sort: (a: CcmOfferRow, b: CcmOfferRow) => (
    getSortingByMetrics(ENGAGEMENT)(a.creatorProfile, b.creatorProfile)
  ),
  render: ({ creatorProfile }: CcmOfferRow) => {
    const channels = creatorProfile.channels;
    const filteredChannels = getFilteredChannels(
      channels,
      CCM_CHANNEL_TYPES,
      false
    );

    return (
      <div className="subcampaigns-table__cell-rows">
        {
          filteredChannels.map(channelType => {
            const channel = channels[channelType];
            const metrics = channel.metrics;
            const engagement = getFormattedPercentageRate(metrics.engagement);

            const containerClasses = classnames('subcampaigns-table__cell-row', {
              'subcampaigns-table__cell-row--centered': true,
              [`subcampaigns-table__cell-${channelType}-metric`]: true
            });

            return (
              <div
                key={channelType}
                className={containerClasses}
              >
                <div className="subcampaigns-table__metric">
                  {engagement}
                </div>
              </div>
            );
          })
        }
      </div>
    );
  }
});

const getResponseTimeColumn = (
  activeTab?: CcmOfferTab
) => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.response_time`),
  key: 'response_time',
  width: activeTab !== 'accepted' ? 112 : 100,
  customizable: true,
  sort: (a: CcmOfferRow, b: CcmOfferRow, ascending?: boolean) => {
    const responseTimeA = (a.creatorProfile.isLightweight)
      ? 'lightweight'
      : mapResponseTypeToCategory(a.responseTime);

    const responseTimeB = (b.creatorProfile.isLightweight)
      ? 'lightweight'
      : mapResponseTypeToCategory(b.responseTime);

    if (!responseTimeA) {
      return 1;
    }

    if (!responseTimeB) {
      return -1;
    }

    const compareIndex = RESPONSE_TIME_SORTING_PRIORITY.indexOf(responseTimeA)
      - RESPONSE_TIME_SORTING_PRIORITY.indexOf(responseTimeB);

    if (compareIndex === 0) {
      return ascending
        ? getSortingByMetrics(FOLLOWERS)(b.creatorProfile, a.creatorProfile)
        : getSortingByMetrics(FOLLOWERS)(a.creatorProfile, b.creatorProfile);
    }

    return compareIndex;
  },
  render: ({ creatorProfile, responseTime }: CcmOfferRow) => {
    if (responseTime) {
      return (
        <ResponseTimeInfo
          abbreviatedBrandLabel
          creatorProfile={creatorProfile}
          responseTime={responseTime}
          className="ccm-campaign-offers-table__response-time"
          showQuestionMark
        />
      );
    }

    return null;
  }
});

const getContextMenu = (
  campaignId: number,
  subcampaign: Subcampaign | null | undefined,
  activeTab: CcmOfferTab,
  actions: CcmOfferContextMenuActions
): React.ReactNode => {
  const hasOutfits = hasSubmittedOutfits([subcampaign ?? {} as Subcampaign]);

  const items = [
    ...insertIf(activeTab === 'accepted', {
      type: 'cancel_offer',
      text: __t(`${MSG_KEY}.context_menu.cancel_offer`),
      disabled: hasOutfits,
      meta: {
        subcampaign,
        campaignType: 'ccm',
        redirectTo: `/campaigns/ccm/${campaignId}/participants/accepted?clicked=tab`
      }
    }),
    ...insertIf(activeTab === 'revoked', {
      name: 'remove_from_revoked',
      text: __t(`${MSG_KEY}.context_menu.remove_from_revoked`),
      onClick: () => actions.onRemoveFromRevoked(subcampaign)
    })
  ];

  return (
    <MoreButtonWithMenu
      width={16}
      className="ccm-campaign-offers-table__action-context-menu"
      contextMenuProps={{ items }}
    />
  );
};

const getActionsColumn = (
  activeTab: CcmOfferTab,
  width?: number
) => ({
  title: __t(`${SUBCAMPAIGNS_MSG_KEY}.columns.actions`),
  key: 'actions',
  width,
  render: ({ campaignId, subcampaign, subcampaignId, offerState, hasUnreadMessages, actions }: CcmOfferRow) => {
    if (isNil(subcampaignId) || isNil(offerState)) {
      return null;
    }

    const contextMenu = activeTab !== 'pool'
      ? getContextMenu(campaignId, subcampaign, activeTab, actions)
      : null;

    const containerClasses = classnames('ccm-campaign-offers-table__actions', {
      'ccm-campaign-offers-table__actions--with-context-menu': Boolean(contextMenu)
    });

    const messengerIconClasses = classnames('ccm-campaign-offers-table__action', {
      'ccm-campaign-offers-table__action--with-notification': hasUnreadMessages
    });

    return (
      <div className={containerClasses}>
        <div className={messengerIconClasses}>
          <Tooltip
            message={__t(`${SUBCAMPAIGNS_MSG_KEY}.tooltips.view_messenger`)}
            width="short"
          >
            <Link to={`/campaigns/ccm/${campaignId}/messages?subcampaignId=${subcampaignId}`}>
              <SVGDynamicIcon
                name="speech-bubble-custom-bg"
                customColor="grey"
                clickable
                width={16}
                height={16}
              />
            </Link>
          </Tooltip>
          {
            hasUnreadMessages && (
              <div className="ccm-campaign-offers-table__notification">
                <NotificationDot bordered />
              </div>
            )
          }
        </div>
        {contextMenu}
      </div>
    );
  }
});

const getOutfitName = () => ({
  title: __t(`${MSG_KEY}.columns.outfit_name`),
  key: 'outfit-name',
  width: 240,
  render: ({ outfits }: CcmOfferRow) => {
    const outfit = padOutfits(outfits)[0];

    const OutfitNodes = (outfit.outfitMetadata || [])
      .map(({ name, status }, index) => (
        <div
          key={`ccm-campaign-offers-table__outfit-name-${name}-${status}-${index}`}
          title={name}
          className="text-overflow-ellipsis"
        >
          {name}
        </div>
      ));

    return (
      <div className="ccm-campaign-offers-table__outfit-name text-overflow-ellipsis">
        {isEmpty(OutfitNodes) ? '-' : OutfitNodes}
      </div>
    );
  }
});

const getOutfitStatus = () => ({
  title: __t(`${MSG_KEY}.columns.outfit_status`),
  key: 'outfit-status',
  width: 112,
  render: ({ outfits }: CcmOfferRow) => {
    const outfit = padOutfits(outfits)[0];

    const OutfitNodes = (outfit.outfitMetadata || [])
      .map(({ name, status = '-' }) => {
        const value = __t(`${MSG_KEY}.labels.${status}`);

        return (
          <div
            key={`ccm-campaign-offers-table__outfit-status-${name}-${value}`}
            title={value}
          >
            {
              status === '-' ? status : (
                <StatusLabel
                  size="tiny"
                  type={STATUS_LABEL_MAP[status]}
                  label={value}
                />
              )
            }
          </div>
        );
      });

    return (
      <div className="ccm-campaign-offers-table__outfit-status">
        {isEmpty(OutfitNodes) ? '-' : OutfitNodes}
      </div>
    );
  }
});

const getOutfitSkuPercent = () => ({
  title: __t(`${MSG_KEY}.columns.outfit_sku_percent`),
  key: 'outfit-sku-percent',
  width: 62,
  render: ({ outfits }: CcmOfferRow) => {
    const outfit = padOutfits(outfits)[0];

    const OutfitNodes = (outfit.outfitMetadata || [])
      .map(({ name, skuPercent }, index) => {
        const value = skuPercent ? `${getPercentage(skuPercent, 1)}%` : '-';

        return (
          <div
            key={`ccm-campaign-offers-table__outfit-sku-percent-${name}-${value}-${index}`}
            title={value}
          >
            {value}
          </div>
        );
      });

    return (
      <div className="ccm-campaign-offers-table__outfit-sku-percent">
        {isEmpty(OutfitNodes) ? '-' : OutfitNodes}
      </div>
    );
  }
});

const getOutfitClicks = () => ({
  title: __t(`${MSG_KEY}.columns.outfit_clicks`),
  key: 'outfit-clicks',
  width: 96,
  render: ({ outfits }: CcmOfferRow) => {
    const outfit = padOutfits(outfits)[0];

    const OutfitNodes = (outfit.outfitMetadata || [])
      .map(({ name, clicks }, index) => {
        const value = clicks ? `${addThousandsSeparator(clicks)}` : '-';

        return (
          <div
            key={`ccm-campaign-offers-table__outfit-clicks-${name}-${value}-${index}`}
            title={value}
          >
            {value}
          </div>
        );
      });

    return (
      <div className="ccm-campaign-offers-table__outfit-clicks">
        {isEmpty(OutfitNodes) ? '-' : OutfitNodes}
      </div>
    );
  }
});

const getOutfitCompensation = () => ({
  title: __t(`${MSG_KEY}.columns.outfit_compensation`),
  key: 'outfit-compensation',
  width: 114,
  render: ({ outfits }: CcmOfferRow) => {
    const outfit = padOutfits(outfits)[0];

    const OutfitNodes = (outfit.outfitMetadata || [])
      .map(({ name, compensation }, index) => {
        const value = compensation ? `${renderPrice(compensation)}` : '-';

        return (
          <div
            key={`ccm-campaign-offers-table__outfit-compensation-${name}-${value}-${index}`}
            title={value}
          >
            {value}
          </div>
        );
      });

    return (
      <div className="ccm-campaign-offers-table__outfit-compensation">
        {isEmpty(OutfitNodes) ? '-' : OutfitNodes}
      </div>
    );
  }
});

export const getCcmColumnsByState = (
  data: Array<CcmOfferResult>,
  activeTab: CcmOfferTab
): Array<SortableTableColumnType> => {
  if (activeTab === 'pool') {
    return [
      getCreatorColumn(),
      getCountryColumn(),
      getWideChannelColumn(),
      getTierColumn(),
      getFollowersColumn(),
      getEngagementColumn(),
      getResponseTimeColumn(),
      ...insertIf(
        data.some(item => !isNil(item.offerState)),
        getActionsColumn(activeTab, 60)
      )
    ];
  }

  if (activeTab === 'accepted') {
    return [
      getCreatorColumn(activeTab),
      getCountryColumn(activeTab),
      getChannelColumn(),
      getFollowersColumn(activeTab),
      getEngagementColumn(activeTab),
      getOutfitName(),
      getOutfitStatus(),
      getOutfitSkuPercent(),
      getOutfitClicks(),
      getOutfitCompensation(),
      getActionsColumn(activeTab)
    ];
  }

  if (activeTab === 'revoked') {
    return [
      getCreatorColumn(),
      getCountryColumn(),
      getWideChannelColumn(),
      getTierColumn(),
      getFollowersColumn(),
      getEngagementColumn(),
      getResponseTimeColumn(),
      getActionsColumn(activeTab)
    ];
  }

  return [];
};
